import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { map } from 'lodash'
import { useReportingForParam } from '../../../../hooks/useReportingForMonth'
import useReports from '../../../../hooks/useReports'
import useStages from '../../../../hooks/useStages'
import FirestoreUser, { StageStatus } from '../../../../types/FirestoreUser'
import TableCell from './TableCell'

export const StagesTable = ({
  update,
  user,
}: {
  update?: (val: {
    id: string
    status: StageStatus
    completions?: ({ month: number; year: number } | null)[]
  }) => void
  user: FirestoreUser
}) => {
  const stages = useStages()
  const reports = useReports()
  const reportingFor = useReportingForParam()
  const userStages = user?.stages || {}

  // Helper function to get stage data with backward compatibility
  const getStageData = (stageId: string) => {
    const stageValue = userStages[stageId]
    if (!stageValue) {
      return {
        status: 'unstarted' as StageStatus,
        completions: [null, null, null],
      }
    }

    // Backward compatibility: if it's just a status string
    if (typeof stageValue === 'string') {
      const completions: ({ month: number; year: number } | null)[] = [
        null,
        null,
        null,
      ]

      // If completed, try to find the completion month from reports and convert YYYY-MM to {month, year}
      if (stageValue === 'complete') {
        map(reports, (report, month) => {
          if (report?.stageProgress?.[stageId] === 'complete') {
            const [year, monthStr] = month.split('-')
            completions[0] = {
              month: parseInt(monthStr) - 1,
              year: parseInt(year),
            } // Convert to 0-based month
          }
        })
      }

      return { status: stageValue, completions }
    }

    // New format: StageData object
    const completions = [...(stageValue.completions || [])]
    while (completions.length < 3) {
      completions.push(null)
    }
    return { status: stageValue.status, completions }
  }

  const handleCompletionUpdate = (
    stageId: string,
    completionIndex: number,
    date: { month: number; year: number } | null
  ) => {
    if (!update) return

    const currentData = getStageData(stageId)
    const newCompletions = [...currentData.completions]
    newCompletions[completionIndex] = date

    // Determine new status based on completions
    const hasAnyCompletion = newCompletions.some(
      (completion) => completion !== null
    )
    const newStatus: StageStatus = hasAnyCompletion ? 'complete' : 'unstarted'

    update({
      id: stageId,
      status: newStatus,
      completions: newCompletions,
    })
  }

  return (
    <TableContainer>
      <Table size='sm'>
        <Thead>
          <Tr>
            <Th width='15%' borderBottom='none'></Th>
            <Th borderBottom='none'></Th>
            <Th colSpan={3} textAlign='left' borderBottom='none'>
              Reviewed Completely and Action Taken
            </Th>
          </Tr>
          <Tr>
            <Th width='15%'>Step</Th>
            <Th>Title</Th>
            <Th>1st Time</Th>
            <Th>2nd Time</Th>
            <Th>3rd Time</Th>
          </Tr>
        </Thead>
        <Tbody>
          {stages?.map((stage, index) => {
            const stageData = getStageData(stage.id)
            const { status, completions } = stageData

            let allowEdit = true
            if (status === 'complete') {
              map(reports, (report, month) => {
                if (report?.stageProgress?.[stage.id] === 'complete') {
                  if (reportingFor) {
                    allowEdit = month >= reportingFor
                  }
                }
              })
            }

            return (
              <Tr key={stage.id}>
                <Td>{index === 0 ? '--' : index}</Td>
                <Td>{stage.title}</Td>
                <TableCell
                  value={completions[0] || null}
                  onUncheck={() => handleCompletionUpdate(stage.id, 0, null)}
                  editable={allowEdit && !!update}
                  onCheck={(date) => handleCompletionUpdate(stage.id, 0, date)}
                  modalTitle='Step Complete'
                  modalSubtitle={`When did you complete ${stage.title}?`}
                  completionIndex={0}
                />
                <TableCell
                  value={completions[1] || null}
                  onUncheck={() => handleCompletionUpdate(stage.id, 1, null)}
                  editable={
                    !!update &&
                    !!completions[0] &&
                    (allowEdit || !completions[1])
                  }
                  onCheck={(date) => handleCompletionUpdate(stage.id, 1, date)}
                  modalTitle='Step Complete'
                  modalSubtitle={`When did you complete ${stage.title}?`}
                  firstCompletionDate={completions[0]}
                  completionIndex={1}
                />
                <TableCell
                  value={completions[2] || null}
                  onUncheck={() => handleCompletionUpdate(stage.id, 2, null)}
                  editable={
                    !!update &&
                    !!completions[1] &&
                    (allowEdit || !completions[2])
                  }
                  onCheck={(date) => handleCompletionUpdate(stage.id, 2, date)}
                  modalTitle='Step Complete'
                  modalSubtitle={`When did you complete ${stage.title}?`}
                  firstCompletionDate={completions[1]}
                  completionIndex={2}
                />
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

export default StagesTable
